import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Turnstile } from '@marsidev/react-turnstile'
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'
import { contactSchema, type ContactInput } from '@hun-tech/shared/schemas'
import { postContact, ApiErrorResponse } from '@/lib/api'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Button } from '@/components/ui/Button'

// Cloudflare Turnstile Always-Pass test sitekey used for local/staging when production key is not passed
const TURNSTILE_SITEKEY =
  import.meta.env.VITE_TURNSTILE_SITEKEY || '1x00000000000000000000AA'

export function ContactFormSection() {
  const [submissionStatus, setSubmissionStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle')
  const [statusMessage, setStatusMessage] = useState<string>('')

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ContactInput>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      subject: '',
      message: '',
      turnstileToken: '',
    },
  })

  // Update form when turnstile generates token
  const handleTurnstileSuccess = (token: string) => {
    setValue('turnstileToken', token, { shouldValidate: true })
  }

  const onSubmit = async (data: ContactInput) => {
    setSubmissionStatus('loading')
    setStatusMessage('')

    try {
      const result = await postContact(data)
      setSubmissionStatus('success')
      setStatusMessage(
        result.message ||
          'Your transmission was received. We will respond within 24 hours.'
      )
      reset()
    } catch (error) {
      setSubmissionStatus('error')
      if (error instanceof ApiErrorResponse) {
        setStatusMessage(
          error.errorData.error || 'Failed to submit the form. Please verify and try again.'
        )
      } else {
        setStatusMessage('Network connectivity error. Please try again shortly.')
      }
    }
  }

  return (
    <div className="rounded-sm border border-border bg-surface-1 p-5 sm:p-8 md:p-10">
      <h2 className="text-lg sm:text-xl font-bold tracking-tight text-fg mb-5 sm:mb-6">
        Project Specification Form
      </h2>

      {/* Status Feedback Banner */}
      {submissionStatus === 'success' && (
        <div className="mb-5 sm:mb-6 flex items-start gap-3 rounded-sm border border-success/30 bg-success-bg p-4 text-sm text-success">
          <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
          <p>{statusMessage}</p>
        </div>
      )}

      {submissionStatus === 'error' && (
        <div className="mb-5 sm:mb-6 flex items-start gap-3 rounded-sm border border-error/30 bg-error-bg p-4 text-sm text-error">
          <AlertCircle size={18} className="mt-0.5 shrink-0" />
          <p>{statusMessage}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-5" noValidate>
        {/* Full Name */}
        <Input
          label="Your Name or Organization"
          placeholder="e.g. Alex Chen or Nexus Enterprises"
          error={errors.name?.message}
          disabled={submissionStatus === 'loading'}
          {...register('name')}
        />

        {/* Email */}
        <Input
          label="Corporate Email Address"
          type="email"
          placeholder="name@company.com"
          error={errors.email?.message}
          disabled={submissionStatus === 'loading'}
          {...register('email')}
        />

        {/* Subject */}
        <Input
          label="Subject / Project Nature"
          placeholder="e.g. Enterprise ERP Migration or Mobile App Build"
          error={errors.subject?.message}
          disabled={submissionStatus === 'loading'}
          {...register('subject')}
        />

        {/* Message */}
        <Textarea
          label="Project Scope & Technical Requirements"
          rows={5}
          placeholder="Outline your timeline, current architecture, key modules, or expected milestones..."
          error={errors.message?.message}
          disabled={submissionStatus === 'loading'}
          {...register('message')}
        />

        {/* Cloudflare Turnstile Bot Protection */}
        <div className="pt-2 overflow-x-auto max-w-full">
          <Turnstile
            siteKey={TURNSTILE_SITEKEY}
            onSuccess={handleTurnstileSuccess}
            onError={() => {
              setValue('turnstileToken', '', { shouldValidate: true })
            }}
            onExpire={() => {
              setValue('turnstileToken', '', { shouldValidate: true })
            }}
          />
          {errors.turnstileToken && (
            <span className="text-xs font-medium text-error mt-1 block">
              {errors.turnstileToken.message}
            </span>
          )}
        </div>

        {/* Submit Action */}
        <div className="pt-2">
          <Button
            type="submit"
            disabled={submissionStatus === 'loading'}
            fullWidth
            size="lg"
            className="min-h-[48px] shadow-xs"
          >
            {submissionStatus === 'loading' ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Transmitting Inquiry...</span>
              </>
            ) : (
              <span>Submit Project Specification</span>
            )}
          </Button>
        </div>
      </form>
    </div>
  )
}
